import type { AssessmentPortalConfig, FlowStepDefinition } from '../../assessment-portal';
import type { AdhdFlowState } from '../types/flow';
import {
  ADHD_BASE,
  ADHD_BOOKING_PATH,
  ASSESSMENT_TYPE,
  QUESTIONNAIRE_TEMPLATE_ID,
  CONSENT_CATEGORY_MAP,
  consentItems,
  AdhdFlowStep,
} from '../data/constants';
import { ConsentPage, MedicalHistoryPage } from '../pages';
import {
  WelcomeStep,
  CarePathwayStep,
  DetailsStep,
  FeeEstimateStep,
  BookingStep,
  BookingConfirmationStep,
} from '../pages/steps';
import { trackBackClick } from '../utils/booking-analytics';

const createAdhdInitialFlowState = (): AdhdFlowState => ({
  consent: {
    multiAppt: false,
    emergency: false,
    privacy: false,
    cancellation: false,
  },
  patientInfo: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    email: '',
    mobile: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    cultural: '',
    referral: '',
    emergencyName: '',
    emergencyRelationship: '',
    emergencyMobile: '',
    medicareType: '',
    medicareNumber: '',
    medicareRef: '',
    medicareExpiry: '',
    dvaNumber: '',
    dvaCardType: '',
  },
  fee: {
    state: '',
    location: '',
    feeAssessmentScope: '',
    prescriptionType: '',
    seenPsychiatrist: '',
  },
  medicalHistory: {
    occupation: '',
    marital: '',
    children: '',
    living: '',
    seenSpecialist: [],
    seenPsychiatrist12m: '',
    psychiatristDetail: '',
    reasonForConsult: [],
    reasonOther: '',
    declinedDiagnosis: '',
    prevReportFile: null,
    prevDiagnosis: [],
    otherDiagnosis: '',
    consultReasonNotReferral: [],
    consultReasonNotReferralOther: '',
    familyHistory: '',
    physicalConditions: '',
    medications: '',
    substances: '',
    docs: [null, null, null],
  },
  questionnaireAnswers: {},
  questionnaireMeta: {
    templateId: QUESTIONNAIRE_TEMPLATE_ID,
    templateVersion: 1,
  },
  booking: {
    clinicianId: '',
    clinicianName: '',
    clinicianTitle: '',
    timezone: '',
    startDateTime: '',
    endDateTime: '',
  },
  submissionResult: {
    isSuccess: false,
    errorMessage: '',
  },
  carePathway: {
    isAccepted: false,
  },
});

const adhdFlowSteps: FlowStepDefinition[] = [
  {
    key: 'welcome',
    path: '',
    label: 'Welcome',
    component: WelcomeStep,
    layout: 'full',
  },
  {
    key: 'carePathway',
    path: '/care-pathway',
    label: 'Care Pathway',
    component: CarePathwayStep,
    layout: 'full',
  },
  {
    key: 'consent',
    path: '/consent',
    label: 'Consent',
    trackerLabel: 'Consent',
    mobileTitle: 'Consents & agreements',
    mobileSubtitle: 'Accept each item to continue',
    component: ConsentPage,
    layout: 'with-tracker',
  },
  {
    key: 'patientInfo',
    path: '/details',
    label: 'Details',
    trackerLabel: 'Details',
    mobileTitle: 'Your details',
    mobileSubtitle: 'Contact and Medicare',
    component: DetailsStep,
    layout: 'with-tracker',
  },
  {
    key: 'fee',
    path: '/fee',
    label: 'Fee Estimate',
    trackerLabel: 'Fee',
    mobileTitle: 'Your fee estimate',
    mobileSubtitle: 'Private fee',
    component: FeeEstimateStep,
    layout: 'with-tracker',
  },
  {
    key: 'medicalHistory',
    path: '/medical',
    label: 'Medical History',
    trackerLabel: 'Medical',
    mobileTitle: 'Medical history',
    mobileSubtitle: 'A few health questions',
    component: MedicalHistoryPage,
    layout: 'with-tracker',
  },
  {
    key: 'booking',
    path: ADHD_BOOKING_PATH,
    label: 'Booking',
    trackerLabel: 'Booking',
    mobileTitle: 'Book your intake',
    mobileSubtitle: 'Telehealth · 40–45 min',
    component: BookingStep,
    layout: 'with-tracker',
  },
  {
    key: 'bookingConfirmation',
    path: '/booked',
    label: 'Booking Confirmation',
    component: BookingConfirmationStep,
    layout: 'full',
  },
];

export const adhdPortalConfig: AssessmentPortalConfig<AdhdFlowState> = {
  specialtyKey: 'adhd',
  assessmentType: ASSESSMENT_TYPE,
  specialtyId: process.env.REACT_APP_ADHD_SPECIALTY_ID as string,
  questionnaireTemplateId: QUESTIONNAIRE_TEMPLATE_ID,

  basePath: ADHD_BASE,
  flowSteps: adhdFlowSteps,

  consentItems,
  consentCategoryMap: CONSENT_CATEGORY_MAP,

  buildSubmissionPayload: (flowState, feeEstimate, questionnaireMeta) => ({
    flowState,
    feeEstimate,
    questionnaireMeta,
  }),

  createInitialFlowState: createAdhdInitialFlowState,

  // Back on the booking page itself is tracked in BookingPage; this covers the mobile header.
  onHeaderBack: (step) => {
    if (step.key === AdhdFlowStep.BOOKING) trackBackClick();
  },

  unloadWarningUntilReachedStepKey: AdhdFlowStep.BOOKING_CONFIRMATION,

  reloadReset: {
    toStepKey: 'welcome',
    excludeStepKeys: ['bookingConfirmation'],
  },

  theme: {
    cssClass: 'adhd-portal',
  },
};
